import { useEffect, useRef, useState } from 'react';
import Countdown from 'react-countdown';
import { useNavigate } from 'react-router-dom';

import Button from '../ui/button';
import { extractDiscountCodeInfoFromUrl } from '../utils/utils';

type Props = {
	finalPrice: number;
	quantity: number;
	discountUrl: string;
	discountDuration: number;
	redirectToLocalCheckout?: boolean;
	productId?: string;
	storeName?: string;
	discount?: any;
	isCSVHaggle?: any;
	vendorEmail?: string;
};

const STORAGE_KEY = 'chat-discount-end-time';

export default function EndChatButton({
	finalPrice,
	quantity,
	discountUrl,
	discountDuration,
	redirectToLocalCheckout,
	productId,
	storeName,
	discount,
	isCSVHaggle,
	vendorEmail,
}: Readonly<Props>) {
	const [isExpired, setIsExpired] = useState(false);
	const [endTime, setEndTime] = useState<number | null>(null);
	const countdownRef = useRef<Countdown>(null);
	const navigate = useNavigate();

	useEffect(() => {
		const savedEndTime = localStorage.getItem(STORAGE_KEY);

		if (savedEndTime) {
			setEndTime(parseInt(savedEndTime, 10));
		} else {
			const newEndTime = Date.now() + discountDuration * 60 * 1000;
			localStorage.setItem(STORAGE_KEY, newEndTime.toString());
			setEndTime(newEndTime);
		}
	}, [discountDuration]);

	const handlePrice = () => {
		return finalPrice * quantity;
	};

	const handleComplete = () => {
		setIsExpired(true);
		localStorage.removeItem(STORAGE_KEY);
		if (countdownRef.current) {
			countdownRef.current.stop();
		}
	};

	useEffect(() => {
		// Remove the countdown end time from localStorage when new session begins
		localStorage.removeItem(STORAGE_KEY);
	}, []);

	const renderer = ({
		minutes,
		seconds,
		completed,
	}: {
		minutes: number;
		seconds: number;
		completed: boolean;
	}) => {
		if (completed || isExpired) {
			return <span className="text-red-500">This special offer has ended</span>;
		}

		const isUnderOneMinute = minutes === 0;

		return (
			<span
				className={`${
					isUnderOneMinute ? 'text-red-600 animate-pulse' : 'text-red-500'
				}`}
			>
				Offer expires in {minutes}m {seconds}s
			</span>
		);
	};

	// Don’t render button until endTime is loaded
	if (!endTime) return null;

	const handleClick = async () => {
		if (isExpired) return;
		const discountCodeURLInfo = await extractDiscountCodeInfoFromUrl(
			discountUrl,
		);
		if (discountUrl) {
			if (redirectToLocalCheckout) {
				navigate('/checkout', {
					state: {
						vendorEmail,
						finalPrice,
						quantity: quantity,
						discountDuration,
						productId,
						storeName,
						variantId:
							discountCodeURLInfo && discountCodeURLInfo.variantId
								? discountCodeURLInfo.variantId
								: discount?.productVariantId,
						discountCode:
							discountCodeURLInfo && discountCodeURLInfo.discountCode
								? discountCodeURLInfo.discountCode
								: discount?.code,
						isCSVHaggle: isCSVHaggle,
					},
				});
			} else {
				window.open(discountUrl, '_blank');
			}
		} else {
			return;
		}
	};

	return (
		<Button
			onClick={handleClick}
			disabled={isExpired}
			data-test="end-chat-button"
			className={`p-8 rounded w-full text-black ${
				isExpired ? 'pointer-events-none opacity-80 cursor-not-allowed' : ''
			}`}
			style={{ backgroundColor: isExpired ? '#ccc' : 'rgb(114,219,220)' }}
			shape="none"
			transition="none"
		>
			{isExpired ? (
				<p className="text-center text-black">Offer expired</p>
			) : (
				<p className="text-center text-black">
					Go to checkout and buy {quantity} units for ${handlePrice()}
				</p>
			)}
			<p className="mt-1 font-semibold">
				<Countdown
					date={endTime}
					renderer={renderer}
					onComplete={handleComplete}
					ref={countdownRef}
				/>
			</p>
		</Button>
	);
}
